import DataProvider from './DataProvider';
import { DEFAULT_FROM_BLOCK, RINKEBY_FROM_BLOCK, SEPOLIA_FROM_BLOCK, KNOWN_MINT_BLOCK_RANGES, LAST_KNOWN_MINT_BLOCK, INCREMENTAL_SCAN_FROM_BLOCK } from './blockRanges';
import { getPlatformConfigForNetwork } from '../config/platformConfig';
import { fetchCSSFromIPFS } from '../utils/ipfs';
import { fetchSelfHostedProjectCSS } from '../utils/projectAssets';

import ImmutablesArt from '../contracts/ImmutablesArt.json';
import ImmutablesPage from '../contracts/Immutables.json';

import Web3 from 'web3';

// Widest eth_getLogs window to ask for, as (toBlock - fromBlock). MetaMask
// rejects anything over 10,000 with "range N exceeds limit of 10000", and other
// wallets have their own limits, so windows halve themselves down from here
// when refused. Below MIN_BLOCK_SPAN a refusal is no longer about width.
const MAX_BLOCK_SPAN = 9999;
const MIN_BLOCK_SPAN = 499;
// How far back the targeted search will walk before giving up and letting the
// broad scan take over. Sixty windows is roughly three months of blocks: far
// more than any gap between snapshot refreshes, and far less than the years a
// first visit with no snapshot would need.
const MAX_BACKWARD_WINDOWS = 60;
// How far either side of the script transaction, and back from the head, to
// look for page posts. They cluster around the script; a later edit is by
// definition recent.
const PAGE_POST_MARGIN = 100000;

/**
 * BlockchainProvider — reads all data from live blockchain via web3.
 */
class BlockchainProvider extends DataProvider {
  constructor() {
    super();
    this.web3 = null;
    this.contract = null;
    this.pageContract = null;
    this.networkId = null;
    this.deployedNetwork = null;
    this.accounts = null;
    this.fromBlock = DEFAULT_FROM_BLOCK;
    // Whether the last read came back short, and by how much. An edition with
    // no mint hash cannot be drawn at all, so this is the difference between
    // "that is all of them" and "we could not find out".
    this.lastScanIncomplete = false;
    this.missingEditionCount = 0;
  }

  get sourceType() { return 'blockchain'; }
  get isReadOnly() { return false; }

  /**
   * Initialize web3 connection and contract instance.
   * @returns {Promise<{success: boolean, needsModal: string|null, web3: object, contract: object, networkId: number, accounts: string[], deployedNetwork: object, fromBlock: number}>}
   */
  async initialize() {
    // Try to connect web3
    const web3Result = await this._connectWeb3();
    if (!web3Result.success) {
      return web3Result;
    }

    // Set up contract
    const setupResult = await this._setupContract();
    return setupResult;
  }

  async _connectWeb3() {
    if (window.ethereum) {
      this.web3 = new Web3(window.ethereum);
      // eth_requestAccounts is the EIP-1102 method. ethereum.enable() is
      // deprecated and warned about on every load, and when it is eventually
      // removed a wallet would simply stop connecting; the fallback keeps
      // older injected providers working in the meantime.
      if (typeof window.ethereum.request === 'function') {
        await window.ethereum.request({ method: 'eth_requestAccounts' });
      } else if (typeof window.ethereum.enable === 'function') {
        await window.ethereum.enable();
      }
    } else {
      // No wallet extension — fail fast so archive fallback can kick in
      return { success: false, needsModal: 'metamask' };
    }

    return { success: true, needsModal: null };
  }

  async _setupContract() {
    const web3 = this.web3;
    const accounts = await web3.eth.getAccounts();
    const networkId = await web3.eth.net.getId();
    const deployedNetwork = ImmutablesArt.networks[networkId];

    if (!deployedNetwork) {
      return { success: false, needsModal: 'chain', accounts, networkId };
    }

    const contract = new web3.eth.Contract(
      ImmutablesArt.abi,
      deployedNetwork.address,
    );

    const deployedPageNetwork = ImmutablesPage.networks[networkId];
    const pageContract = new web3.eth.Contract(
      ImmutablesPage.abi,
      deployedPageNetwork && deployedPageNetwork.address,
    );

    let fromBlock = DEFAULT_FROM_BLOCK;
    if (networkId === 4) {
      fromBlock = RINKEBY_FROM_BLOCK;
    } else if (networkId === 11155111) {
      fromBlock = SEPOLIA_FROM_BLOCK;
    }

    this.web3 = web3;
    this.contract = contract;
    this.pageContract = pageContract;
    this.networkId = networkId;
    this.deployedNetwork = deployedNetwork;
    this.accounts = accounts;
    this.fromBlock = fromBlock;

    return {
      success: true,
      needsModal: null,
      web3,
      contract,
      pageContract,
      networkId,
      deployedNetwork,
      accounts,
      fromBlock,
    };
  }

  async getPlatformConfig() {
    const contract = this.contract;
    const web3 = this.web3;

    const [
      maxTotalSupply, currentTokenId, currentProjectId,
      artistPercent, immutablesWEB, immutablesURI, useMetadataServer,
      curator, curatorPercent, beneficiary, beneficiaryPercent,
      projectFeeInWei, featuredProjectId, secondaryRoyaltyPercent,
    ] = await Promise.all([
      contract.methods.maxTotalSupply().call(),
      contract.methods.currentTokenId().call(),
      contract.methods.currentProjectId().call(),
      contract.methods.artistPercent().call(),
      contract.methods.immutablesWEB().call(),
      contract.methods.immutablesURI().call(),
      contract.methods.useMetadataServer().call(),
      contract.methods.curator().call(),
      contract.methods.curatorPercent().call(),
      contract.methods.beneficiary().call(),
      contract.methods.beneficiaryPercent().call(),
      contract.methods.projectFee().call(),
      contract.methods.featuredProjectId().call(),
      contract.methods.secondaryRoyaltyPercent().call(),
    ]);

    const projectFeeInEth = web3.utils.fromWei(projectFeeInWei, 'ether');
    const platform = getPlatformConfigForNetwork(this.networkId);

    return {
      platform,
      maxTotalSupply,
      currentTokenId,
      currentProjectId,
      artistPercent,
      immutablesWEB,
      immutablesURI,
      useMetadataServer,
      curator,
      curatorPercent,
      beneficiary,
      beneficiaryPercent,
      projectFeeInWei,
      projectFeeInEth,
      featuredProjectId,
      secondaryRoyaltyPercent,
    };
  }

  async getUserRoles(account, projectId) {
    const contract = this.contract;

    const [contractOwner, isTeammember, userIsAuthorizedArtist, artistScreeningEnabled] = await Promise.all([
      contract.methods.owner().call(),
      contract.methods.isTeammember(account).call(),
      contract.methods.isAuthorizedArtist(account).call(),
      contract.methods.artistScreeningEnabled().call(),
    ]);

    const [curator, beneficiary] = await Promise.all([
      contract.methods.curator().call(),
      contract.methods.beneficiary().call(),
    ]);

    const userIsContractOwner = (contractOwner === account);
    const userIsPayee = (contractOwner === account || curator === account || beneficiary === account);
    const userIsAdmin = (contractOwner === account || curator === account || isTeammember);

    let userIsProjectArtist = false;
    if (projectId) {
      const projectArtistAddress = await contract.methods.projectIdToArtistAddress(projectId).call();
      userIsProjectArtist = (account === projectArtistAddress);
    }

    return {
      userIsAdmin,
      userIsContractOwner,
      userIsPayee,
      userIsAuthorizedArtist,
      userIsProjectArtist,
      artistScreeningEnabled,
      contractOwner,
    };
  }

  async getProjects() {
    const contract = this.contract;
    const currentProjectId = await contract.methods.currentProjectId().call();

    const projects = [];
    const artists = [];
    for (let i = 1; i <= currentProjectId; i++) {
      const [project, artist] = await Promise.all([
        contract.methods.projects(i).call(),
        contract.methods.projectIdToArtistAddress(i).call(),
      ]);
      projects.push(project);
      artists.push(artist);
    }

    return { projects, artists };
  }

  async getProjectDetails(projectId) {
    const contract = this.contract;
    const web3 = this.web3;
    const projectIdNum = parseInt(projectId, 10);

    const [
      projectArtistAddress,
      projectPricePerEditionInWei,
      projectAdditionalPayee,
      projectAdditionalPayeePercent,
      projectRoyaltyAddress,
      projectIdToImageURLBase,
      projectIdToImageURLExt,
      projectIdUseImageURLInGridView,
    ] = await Promise.all([
      contract.methods.projectIdToArtistAddress(projectIdNum).call(),
      contract.methods.projectIdToPricePerEditionInWei(projectIdNum).call(),
      contract.methods.projectIdToAdditionalPayee(projectIdNum).call(),
      contract.methods.projectIdToAdditionalPayeePercent(projectIdNum).call(),
      contract.methods.projectIdToRoyaltyAddress(projectIdNum).call(),
      contract.methods.projectIdToImageURLBase(projectIdNum).call(),
      contract.methods.projectIdToImageURLExt(projectIdNum).call(),
      contract.methods.projectIdUseImageURLInGridView(projectIdNum).call(),
    ]);

    const projectPricePerEditionInEth = web3.utils.fromWei(projectPricePerEditionInWei, 'ether');
    const royaltyContractBalanceWei = await web3.eth.getBalance(projectRoyaltyAddress);
    const royaltyContractBalance = web3.utils.fromWei(royaltyContractBalanceWei, 'ether');

    const clientViewImageURLInDetailView = !!projectIdUseImageURLInGridView;

    return {
      projectArtistAddress,
      projectPricePerEditionInWei,
      projectPricePerEditionInEth,
      projectAdditionalPayee,
      projectAdditionalPayeePercent,
      projectRoyaltyAddress,
      projectIdToImageURLBase,
      projectIdToImageURLExt,
      projectIdUseImageURLInGridView,
      clientViewImageURLInDetailView,
      royaltyContractBalance,
    };
  }

  /**
   * The snapshot this site ships, used as the starting point for a live read.
   *
   * It is the same data the archive path serves, and it records the block it
   * was built to, so the chain only has to be asked about what came after.
   */
  async _loadSnapshot(projectId) {
    const file = `${this.networkId}-${projectId}-tokens.json`;
    const embedded = typeof window !== 'undefined' && window.__ARCHIVE_DATA;
    if (embedded) return embedded[file] || null;
    try {
      const res = await fetch(`${process.env.PUBLIC_URL}/cache/${file}`);
      if (!res.ok) return null;
      const text = await res.text();
      // A host that answers unknown paths with the SPA shell returns 200 + HTML.
      if (text.trim().startsWith('<')) return null;
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  async getProjectTokens(projectId, currentEditions, onProgress) {
    const contract = this.contract;
    const networkId = this.networkId;
    const projectIdNum = parseInt(projectId, 10);

    this.lastScanIncomplete = false;
    this.missingEditionCount = 0;

    let startBlock = this.fromBlock;
    const byEdition = new Map();

    // Start from the shipped snapshot. Without this a first visit has nothing
    // to resume from and scans the whole range from the project's first block,
    // even for a project that sold out years ago.
    if (onProgress) onProgress('loading cached editions ...');
    const snapshot = await this._loadSnapshot(projectId);
    if (snapshot && Array.isArray(snapshot.tokens)) {
      for (const token of snapshot.tokens) {
        if (parseInt(token.editionId, 10) <= currentEditions) {
          byEdition.set(String(token.editionId), { ...token, projectId: String(projectIdNum) });
        }
      }
      if (snapshot.lastBlock) startBlock = snapshot.lastBlock + 1;
    }

    // Anything this browser saw since then wins, and carries a block number.
    if (onProgress) onProgress('loading local storage editions ...');
    for (let j = 1; j <= currentEditions; j++) {
      try {
        const storageName = `${networkId}-${projectId}-${j}`;
        const tokenData = window.localStorage.getItem(storageName);
        const token = JSON.parse(tokenData);
        if (token && String(token.projectId) === String(projectIdNum)) {
          if (token.blockNumber && token.blockNumber + 1 > startBlock) startBlock = token.blockNumber + 1;
          byEdition.set(String(token.editionId), token);
        }
      } catch (error) { }
    }

    let tokens = [...byEdition.values()]
      .sort((a, b) => parseInt(a.editionId, 10) - parseInt(b.editionId, 10));

    const record = (event) => {
      const tokenData = {
        networkId,
        blockNumber: event.blockNumber,
        projectId: event.returnValues.projectId,
        editionId: event.returnValues.editionId,
        tokenId: event.returnValues.tokenId,
        transactionHash: event.transactionHash,
      };
      const storageName = `${tokenData.networkId}-${tokenData.projectId}-${tokenData.editionId}`;
      try {
        window.localStorage.setItem(storageName, JSON.stringify(tokenData));
      } catch (e) { /* a full quota must not lose the edition itself */ }
      byEdition.set(String(tokenData.editionId), tokenData);
    };

    if (tokens.length < currentEditions) {
      // Ask the contract which tokens exist before going anywhere near a log
      // query. That is one call and it is authoritative, which turns "scan the
      // chain for mints since block X and hope" into "fetch these three
      // specific hashes" — and, most of the time, into no log query at all.
      let missing = null;
      try {
        if (onProgress) onProgress('checking for new editions ...');
        const onChain = await contract.methods.getTokenIdsForProjectId(projectIdNum).call();
        const held = new Set([...byEdition.values()].map((t) => String(t.tokenId)));
        missing = onChain.map(String).filter((id) => !held.has(id));
      } catch (error) {
        // An older deployment may not expose the list; fall back to scanning.
        console.warn('[BlockchainProvider] Could not read the project token list:', error.message);
      }

      if (missing && missing.length === 0) {
        // Nothing new. This is the common case and it now costs no logs at all.
      } else {
        const targeted = missing && missing.length > 0
          ? await this._findMintsForTokens(contract, projectIdNum, missing, startBlock, onProgress)
          : null;

        if (targeted && targeted.complete) {
          targeted.events.forEach(record);
        } else {
          // Either the token list was unavailable, or the mints are older than
          // the backwards search covers — a first visit with no snapshot, where
          // the editions are spread over years. Fall back to the broad scan.
          if (onProgress) onProgress('loading editions...');
          if (targeted) targeted.events.forEach(record);
          try {
            const events = await this._queryEvents(
              contract, 'AddressMintedProjectEditionAsToken',
              { projectId: projectIdNum }, startBlock, 'latest', KNOWN_MINT_BLOCK_RANGES,
              (index, total) => {
                if (onProgress) onProgress(`searching for editions, block range ${index}/${total} ...`);
              }
            );
            events.forEach(record);
          } catch (error) {
            this.lastScanIncomplete = true;
            console.error('Error loading editions:', error);
          }
        }
      }

      tokens = [...byEdition.values()]
        .sort((a, b) => parseInt(a.editionId, 10) - parseInt(b.editionId, 10));
    }

    // An edition with no mint hash cannot be drawn: the hash is the seed, and it
    // exists only in log metadata. Saying so is the point — a short list reads
    // as "these are all the editions" when it may mean "we could not find out".
    this.missingEditionCount = Math.max(0, currentEditions - tokens.length);
    if (this.missingEditionCount > 0) {
      this.lastScanIncomplete = true;
      console.error(
        `[BlockchainProvider] ${this.missingEditionCount} of ${currentEditions} editions of project `
        + `${projectIdNum} have no mint hash and cannot be rendered.`
      );
    }

    if (onProgress) onProgress('loading editions 100.00% ...');

    return tokens;
  }

  /**
   * Find the mints for specific tokens by searching backwards from the head.
   *
   * The transaction hash is the seed the art is drawn from, and it exists
   * nowhere in contract storage — only in the metadata of the transaction that
   * minted the token. So logs remain the only source for it. What the token
   * list buys is knowing exactly which hashes are wanted, which makes the
   * search finite: it walks back from the chain head and stops the moment it
   * has them all. Anything minted since a current snapshot is near the head, so
   * that is usually one window.
   */
  async _findMintsForTokens(contract, projectIdNum, tokenIds, floorBlock, onProgress) {
    const wanted = new Set(tokenIds.map(String));
    const events = [];
    const head = await this.web3.eth.getBlockNumber();
    const floor = Math.max(floorBlock || 0, this.fromBlock || 0);

    let windows = 0;
    for (let to = head; to >= floor && wanted.size > 0; to -= (MAX_BLOCK_SPAN + 1)) {
      if (++windows > MAX_BACKWARD_WINDOWS) break;
      const from = Math.max(floor, to - MAX_BLOCK_SPAN);
      if (onProgress) onProgress(`finding ${wanted.size} new edition${wanted.size === 1 ? '' : 's'}, block range ${windows}/${MAX_BACKWARD_WINDOWS} ...`);
      try {
        // tokenId is an indexed argument, so the node filters by bloom rather
        // than returning every mint in the window.
        const found = await this._queryWindow(
          contract, 'AddressMintedProjectEditionAsToken',
          { projectId: projectIdNum, tokenId: [...wanted] }, from, to
        );
        for (const event of found) {
          if (wanted.delete(String(event.returnValues.tokenId))) events.push(event);
        }
      } catch (error) {
        console.warn(`[BlockchainProvider] Backwards window ${from}-${to} failed:`, error.message);
        return { events, complete: false };
      }
      if (from === floor) break;
    }

    return { events, complete: wanted.size === 0 };
  }

  async getProjectScript(scriptTransactionHash) {
    const web3 = this.web3;

    if (!scriptTransactionHash) {
      return { script: null, poster: null, page: null, blockNumber: null };
    }

    let poster, page, script, blockNumber;
    await web3.eth.getTransaction(scriptTransactionHash, function(err, tx) {
      let tx_data = tx.input;
      let input_data = '0x' + tx_data.slice(10);
      let params = web3.eth.abi.decodeParameters(['string', 'string', 'string'], input_data);

      poster = tx.from;
      page = params[0];
      blockNumber = tx.blockNumber;

      let code_content = params[2];
      const code_clean_regex = /```javascript/g;
      let cleaned_code = code_content.replace(code_clean_regex, '```');
      let code_regex = /```([\s\S]*)```/g;
      script = code_regex.exec(cleaned_code)[1];
    }).catch(function(error) { });

    return { script, poster, page, blockNumber };
  }

  async getPagePosts(pageName, scriptBlockNumber, onProgress) {
    if (!pageName || !this.pageContract) return [];

    try {
      const pageHash = this.web3.utils.soliditySha3(pageName);

      // Two bounded windows rather than one open-ended sweep.
      //
      // The lower bound used to be the script's block but the upper bound was
      // "latest", so a script posted in 2022 asked for every block since — over
      // ten million, which no wallet RPC will serve and which the splitter would
      // now walk in a thousand requests. Page posts cluster around the script
      // transaction, and any later edit is by definition recent, so those are
      // the only two places worth looking.
      const head = await this.web3.eth.getBlockNumber();
      const windows = [];
      if (scriptBlockNumber) {
        windows.push([
          Math.max(scriptBlockNumber - PAGE_POST_MARGIN, this.fromBlock),
          Math.min(scriptBlockNumber + PAGE_POST_MARGIN, head),
        ]);
      }
      windows.push([Math.max(head - PAGE_POST_MARGIN, this.fromBlock), head]);

      // Merge if the script is recent enough that the two overlap.
      windows.sort((a, b) => a[0] - b[0]);
      const merged = [];
      for (const [from, to] of windows) {
        const last = merged[merged.length - 1];
        if (last && from <= last[1] + 1) last[1] = Math.max(last[1], to);
        else merged.push([from, to]);
      }

      const seen = new Set();
      const events = [];
      let failed = 0;
      // Counted across all windows so the reader sees one continuous count
      // rather than a progress figure that restarts partway through.
      const totalWindows = merged.reduce(
        (n, [from, to]) => n + Math.max(1, Math.ceil((to - from + 1) / (MAX_BLOCK_SPAN + 1))), 0
      );
      let done = 0;
      const report = () => {
        done++;
        if (onProgress) onProgress(`reading page posts, block range ${done}/${totalWindows} ...`);
      };
      for (const [from, to] of merged) {
        const result = await this._queryRange(this.pageContract, 'AddressPostedPageTagContent', { pageHash }, from, to, report);
        failed += result.failed;
        for (const event of result.events) {
          const id = `${event.transactionHash}:${event.logIndex}`;
          if (seen.has(id)) continue;
          seen.add(id);
          events.push(event);
        }
      }
      if (failed > 0) {
        console.warn(`[BlockchainProvider] ${failed} range(s) failed while reading page posts for "${pageName}".`);
      }

      return events.map(e => ({
        poster: e.returnValues.poster,
        page: e.returnValues.page,
        tag: e.returnValues.tag,
        content: e.returnValues.content,
        blockNumber: e.blockNumber,
        transactionHash: e.transactionHash,
      }));
    } catch (error) {
      console.warn('[BlockchainProvider] getPagePosts failed:', error.message);
      return [];
    }
  }

  async getOwnerOfToken(tokenId) {
    try {
      return await this.contract.methods.ownerOf(tokenId).call();
    } catch (error) {
      return null;
    }
  }

  async getEditionEventHistory(editionId, projectTokens) {
    try {
      if (!this.contract || !projectTokens || !projectTokens[editionId - 1]) {
        return [];
      }

      const tokenId = projectTokens[editionId - 1].tokenId;
      const startBlock = projectTokens[editionId - 1].blockNumber || this.fromBlock;

      const events = await this._queryEvents(this.contract, 'allEvents', { tokenId: tokenId }, startBlock);

      return events.filter(e => String(e.returnValues.tokenId) === String(tokenId));
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  async getTeammembers() {
    return await this._getPaginatedEvents('AdminModifiedTeammembers', 'user', 'isTeammember');
  }

  async getAuthorizedArtists() {
    return await this._getPaginatedEvents('AdminUpdatedAuthorizedArtist', 'user', 'isAuthorizedArtist');
  }

  async _getPaginatedEvents(eventName, keyField, valueField) {
    const result = {};
    try {
      const events = await this._queryEvents(this.contract, eventName, {}, this.fromBlock);
      for (let j = 0; j < events.length; j++) {
        result[events[j].returnValues[keyField]] = events[j].returnValues[valueField];
      }
    } catch (error) {
      console.warn(`[BlockchainProvider] _getPaginatedEvents(${eventName}) failed:`, error.message);
    }
    return result;
  }

  /**
   * Query past events with automatic fallback to paginated chunks if the RPC
   * rejects large block ranges (common on mobile wallets and free RPCs).
   * When knownRanges is provided, the fallback queries only those ranges
   * (plus an incremental scan from INCREMENTAL_SCAN_FROM_BLOCK to latest).
   */
  async _queryEvents(contract, eventName, filter, fromBlock, toBlock = 'latest', knownRanges = null, onProgress = null) {
    this.lastScanIncomplete = false;
    try {
      return await contract.getPastEvents(eventName, { filter, fromBlock, toBlock });
    } catch (error) {
      console.warn(`[BlockchainProvider] Full-range query failed for ${eventName}, falling back to paginated scan:`, error.message);
      const latest = (toBlock === 'latest') ? await this.web3.eth.getBlockNumber() : toBlock;
      let allEvents = [];
      let failed = 0;

      const collect = async (from, to) => {
        if (to < from) return;
        const result = await this._queryRange(contract, eventName, filter, from, to, onProgress);
        allEvents = allEvents.concat(result.events);
        failed += result.failed;
      };

      if (knownRanges && knownRanges.length > 0) {
        // Only the ranges where mints are known to have happened, which skips
        // millions of empty blocks. One of them is wider than the usual cap, so
        // these go through the same splitter as everything else.
        for (const [start, end] of knownRanges.filter(([start]) => start >= fromBlock)) {
          await collect(start, Math.min(end, latest));
        }
        // Then whatever has happened since. This begins at the caller's own
        // starting point when that is later than the constant: with a current
        // snapshot that is a few thousand blocks, where the constant alone
        // asked for four million in a single request and always failed.
        const incrementalFrom = Math.max(fromBlock, INCREMENTAL_SCAN_FROM_BLOCK, LAST_KNOWN_MINT_BLOCK + 1);
        await collect(incrementalFrom, latest);
      } else {
        await collect(fromBlock, latest);
      }

      if (failed > 0) {
        // Worth saying loudly. A partial scan returns a short list, which reads
        // as "nothing was minted" rather than "we could not find out".
        this.lastScanIncomplete = true;
        console.error(
          `[BlockchainProvider] ${failed} block range(s) could not be read for ${eventName}. `
          + 'Results are incomplete — recent editions may be missing.'
        );
      }
      return allEvents;
    }
  }

  /**
   * Walk a block range in windows the provider will accept.
   *
   * Wallet RPCs cap eth_getLogs and do not advertise the cap: MetaMask rejects
   * anything over 10,000 blocks, others differ. So each window starts at the
   * common limit and halves itself whenever it is refused, which finds a
   * workable size without needing to know it in advance, and splits only the
   * windows that actually need splitting.
   */
  async _queryRange(contract, eventName, filter, from, to, onProgress) {
    const events = [];
    let failed = 0;
    const total = Math.max(1, Math.ceil((to - from + 1) / (MAX_BLOCK_SPAN + 1)));
    let index = 0;
    for (let start = from; start <= to; start += MAX_BLOCK_SPAN + 1) {
      const end = Math.min(start + MAX_BLOCK_SPAN, to);
      index++;
      if (onProgress) onProgress(index, total);
      try {
        const found = await this._queryWindow(contract, eventName, filter, start, end);
        events.push(...found);
      } catch (error) {
        console.warn(`[BlockchainProvider] Range ${start}-${end} failed:`, error.message);
        failed++;
      }
    }
    return { events, failed };
  }

  async _queryWindow(contract, eventName, filter, from, to) {
    try {
      return await contract.getPastEvents(eventName, { filter, fromBlock: from, toBlock: to });
    } catch (error) {
      const span = to - from;
      // Below this the window is small enough that the refusal is about
      // something other than its width, so stop splitting and report it.
      if (span <= MIN_BLOCK_SPAN) throw error;
      const middle = from + Math.floor(span / 2);
      const left = await this._queryWindow(contract, eventName, filter, from, middle);
      const right = await this._queryWindow(contract, eventName, filter, middle + 1, to);
      return left.concat(right);
    }
  }

  async getContractBalance() {
    const contractBalanceWei = await this.web3.eth.getBalance(this.contract.options.address);
    const contractBalance = this.web3.utils.fromWei(contractBalanceWei, 'ether');
    return { contractBalance, contractBalanceWei };
  }

  async getProjectCSS(imageURLBase, isMobile, projectId) {
    // Prefer the self-hosted mirror: same origin, no gateway roulette.
    const selfHosted = await fetchSelfHostedProjectCSS(this.networkId, projectId, isMobile);
    if (selfHosted) return selfHosted;

    // Not mirrored — fall back to the project's IPFS CID from chain.
    if (!imageURLBase) return null;
    try {
      const cssPath = isMobile ? "style.mobile.css" : "style.css";
      const ipfsUrl = imageURLBase + cssPath;
      return await fetchCSSFromIPFS(ipfsUrl, 5000);
    } catch (error) {
      console.warn('[BlockchainProvider] CSS fetch failed:', error.message);
      return null;
    }
  }

  /**
   * Subscribe to contract events.
   * @param {string} eventName
   * @param {Object} filter
   * @param {function} callback — (error, data) => void
   */
  /**
   * Listen for an event as it happens.
   *
   * toBlock is not a subscription option — web3 answers "Invalid option:
   * toBlock. Use getPastEvents for specific range." and never starts the
   * listener, so nothing arrived live and a new mint only appeared on reload.
   *
   * Not every provider supports subscriptions at all, and one that does not
   * should cost the caller nothing: the app already reads the chain on load,
   * so a missing listener means updates are late rather than absent.
   */
  subscribeToEvent(eventName, filter, callback) {
    try {
      const emitter = this.contract.events[eventName]({ filter });
      if (emitter && emitter.on) {
        emitter.on('data', (data) => callback(null, data));
        emitter.on('error', (error) => {
          console.warn(`[BlockchainProvider] ${eventName} subscription error:`, error.message);
        });
      }
      return emitter;
    } catch (error) {
      console.warn(`[BlockchainProvider] ${eventName} could not be subscribed to:`, error.message);
      return null;
    }
  }
}

export default BlockchainProvider;
